import React from 'react';
import { Link } from 'react-router-dom';
import {
    FaHome, FaChevronRight, FaArrowRight, FaPhone, FaCheckCircle,
    FaShieldAlt, FaVideo, FaUserShield, FaBell,
    FaFingerprint, FaEye, FaIdCard, FaCogs, FaServer
} from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const DataCenterSecurityPage = () => {
    const services = [
        {
            icon: <FaVideo size={32} />,
            title: 'CCTV & Video Surveillance',
            description: 'Enterprise-grade IP camera systems with AI-powered analytics, motion detection, and 24/7 recording — covering every inch of your data center interior and perimeter.',
            gradient: 'from-blue-600 to-indigo-600'
        },
        {
            icon: <FaFingerprint size={32} />,
            title: 'Access Control & Biometrics',
            description: 'Multi-factor authentication systems including biometric readers, smart card access, and PIN-based entry — restricting access to authorized personnel only.',
            gradient: 'from-cyan-500 to-blue-600'
        },
        {
            icon: <FaUserShield size={32} />,
            title: 'On-Site Security Personnel',
            description: 'Highly trained, licensed security guards providing 24/7 physical presence, visitor screening, patrol services, and immediate threat response.',
            gradient: 'from-green-500 to-emerald-600'
        },
        {
            icon: <FaBell size={32} />,
            title: 'Intrusion Detection Systems',
            description: 'Advanced IDS sensors, motion detectors, vibration alarms, and perimeter breach alerts integrated into a centralized security management platform.',
            gradient: 'from-orange-500 to-red-600'
        },
        {
            icon: <FaEye size={32} />,
            title: '24/7 Remote Monitoring (SOC)',
            description: 'Round-the-clock Security Operations Center monitoring your facility in real time, with instant incident escalation and coordinated emergency response.',
            gradient: 'from-violet-600 to-purple-700'
        },
        {
            icon: <FaShieldAlt size={32} />,
            title: 'Perimeter Security',
            description: 'Physical perimeter hardening including man-traps, bollards, security fencing, anti-tailgating systems, and vehicle access control barriers.',
            gradient: 'from-red-600 to-rose-700'
        },
        {
            icon: <FaIdCard size={32} />,
            title: 'Visitor & Contractor Management',
            description: 'Structured visitor registration, ID verification, temporary access badging, escort protocols, and full audit trails for every person entering your facility.',
            gradient: 'from-teal-500 to-cyan-600'
        },
        {
            icon: <FaServer size={32} />,
            title: 'Cage & Cabinet Security',
            description: 'Individual rack and cage locking systems with electronic access logging, ensuring only authorized technicians can access specific equipment zones.',
            gradient: 'from-indigo-500 to-blue-700'
        },
        {
            icon: <FaCogs size={32} />,
            title: 'Security System Integration',
            description: 'Seamless integration of all security systems — access control, CCTV, alarms, and monitoring — into a unified platform for centralized management and reporting.',
            gradient: 'from-amber-500 to-orange-600'
        }
    ];

    const benefits = [
        'Licensed & Certified Security Professionals',
        'Integrated Physical & Electronic Security',
        '24/7 On-Site & Remote Monitoring',
        'Compliance with SOC 2, ISO 27001 & NIST',
        'Rapid Incident Response Protocols',
        'Scalable Security Solutions',
        'Full Audit Trails & Reporting',
        'Tailored for Mission-Critical Environments'
    ];

    const stats = [
        { value: '24/7', label: 'Monitoring Coverage' },
        { value: '106+', label: 'Sites Secured' },
        { value: '0', label: 'Breach Incidents (Target)' },
        { value: '115+', label: 'Service Points' }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-slate-900 to-blue-950 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full filter blur-3xl opacity-15 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl opacity-10 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <Link to="/services" className="hover:text-white transition-colors">Services</Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-cyan-400">Data Center Security</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-block mb-4 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full">
                                <span className="text-cyan-400 text-sm font-bold tracking-wider">INTEGRATED SECURITY SOLUTIONS</span>
                            </div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                End-to-End Data Center <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Security</span> You Can Trust
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Comprehensive security solutions combining advanced system installations with highly trained security personnel — delivering maximum protection for your infrastructure, equipment, and operations.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300">
                                    Request a Security Assessment
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>

                        {/* Image collage */}
                        <div className="relative">
                            <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="col-span-2 relative overflow-hidden rounded-xl">
                                        <img src="/serviceImages/dc-security/security-hero.jpg" alt="Data Center Security" className="w-full h-52 object-cover" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                                        <span className="absolute bottom-2 left-2 text-white text-xs font-semibold bg-blue-600/80 px-2 py-0.5 rounded-full">Integrated Security</span>
                                    </div>
                                    <div className="relative overflow-hidden rounded-xl">
                                        <img src="/serviceImages/dc-security/cctv-monitoring.jpg" alt="CCTV Monitoring" className="w-full h-32 object-cover hover:scale-105 transition-transform duration-500" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                                        <span className="absolute bottom-2 left-2 text-white text-xs font-semibold bg-cyan-600/80 px-2 py-0.5 rounded-full">CCTV & SOC</span>
                                    </div>
                                    <div className="relative overflow-hidden rounded-xl">
                                        <img src="/serviceImages/dc-security/security-guard.jpg" alt="Security Guard" className="w-full h-32 object-cover hover:scale-105 transition-transform duration-500" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                                        <span className="absolute bottom-2 left-2 text-white text-xs font-semibold bg-indigo-600/80 px-2 py-0.5 rounded-full">On-Site Guards</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-10 border-t border-white/10">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">{stat.value}</div>
                                <div className="text-gray-400 text-sm mt-1">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-3xl p-12 border border-blue-100">
                        <div className="grid lg:grid-cols-2 gap-10 items-center">
                            <div>
                                <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Our Approach</span>
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">A Complete, Integrated Security Strategy</h2>
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    In today's digital world, data centers are among the most critical assets for businesses and organizations. Protecting these facilities requires more than just technology — it requires a complete, integrated security strategy.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    We provide comprehensive security solutions for data centers, combining advanced security system installations with highly trained security personnel to ensure maximum protection for your infrastructure, equipment, and operations.
                                </p>
                            </div>
                            <div>
                                <img
                                    src="/serviceImages/dc-security/access-control.jpg"
                                    alt="Access Control Systems"
                                    className="w-full h-72 object-cover rounded-2xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Comprehensive Data Center Security Services</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From physical security systems to on-site guards — reliable security services tailored for mission-critical environments
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 hover:-translate-y-2"
                            >
                                <div className="p-8">
                                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                        {service.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                                        {service.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed mb-6">
                                        {service.description}
                                    </p>
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-2 text-blue-600 font-semibold group-hover:text-cyan-600 transition-colors"
                                    >
                                        Learn More
                                        <FaArrowRight className="group-hover:translate-x-2 transition-transform" size={12} />
                                    </Link>
                                </div>
                                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="grid grid-cols-2 gap-3">
                            <img src="/serviceImages/dc-security/security-hero.jpg" alt="Data Center Security" className="w-full h-48 object-cover rounded-2xl shadow-lg col-span-2" />
                            <img src="/serviceImages/dc-security/cctv-monitoring.jpg" alt="CCTV Monitoring" className="w-full h-36 object-cover rounded-xl shadow-lg" />
                            <img src="/serviceImages/dc-security/security-guard.jpg" alt="Security Guard" className="w-full h-36 object-cover rounded-xl shadow-lg" />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Why Choose Us</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Why Trust Us With Your Data Center Security</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white flex-shrink-0 mt-0.5">
                                            <FaCheckCircle size={14} />
                                        </div>
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300"
                            >
                                Get a Free Security Assessment
                                <FaArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative bg-gradient-to-r from-slate-900 to-blue-900 py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="/serviceImages/dc-security/security-benefits.jpg" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 to-blue-900/90"></div>
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-full">
                        <FaShieldAlt className="text-cyan-400" size={14} />
                        <span className="text-cyan-400 text-sm font-semibold">Protect What Matters Most</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Ready to Secure Your Data Center?</h2>
                    <p className="text-xl text-gray-300 mb-12">
                        Contact us today for a free security assessment. Our experts will design a customized protection strategy for your facility.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300"
                        >
                            Contact Us Today
                            <FaArrowRight size={14} />
                        </Link>
                        <a
                            href="tel:+12014099845"
                            className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300"
                        >
                            <FaPhone size={14} />
                            Call Us Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default DataCenterSecurityPage;
